// HM-373 cluster 16.4: tab Edifici con CRUD compatto.
// Pattern visivo coerente con ListiniSection/CamereSection esistenti.

import { Pencil, Plus, Trash2, X } from '@hotel-manager/ui/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  type Building,
  createBuilding,
  deleteBuilding,
  listBuildings,
  updateBuilding,
} from '../../../api/buildings.js';
import { ApiError } from '../../../api/client.js';
import { fetchRooms } from '../../../api/rooms.js';

export function EdificiSection() {
  const qc = useQueryClient();
  const buildingsQuery = useQuery({ queryKey: ['buildings'], queryFn: listBuildings });
  const roomsQuery = useQuery({ queryKey: ['rooms'], queryFn: fetchRooms });
  const [editing, setEditing] = useState<Building | 'new' | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Building | null>(null);
  const [errMsg, setErrMsg] = useState<string | null>(null);

  const buildings = buildingsQuery.data?.buildings ?? [];
  const rooms = roomsQuery.data?.rooms ?? [];

  const countActiveRooms = (buildingCode: string): number =>
    rooms.filter((r) => r.building === buildingCode).length;

  const onClose = () => {
    setEditing(null);
    setErrMsg(null);
  };

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteBuilding(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['buildings'] });
      setDeleteTarget(null);
    },
    onError: (err: unknown) => setErrMsg(formatErr(err)),
  });

  return (
    <section data-testid="edifici-section" className="flex flex-col gap-4">
      <header className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-bold text-text-primary">Edifici</h2>
          <p className="text-xs text-text-secondary">
            Gestisci gli edifici dell'hotel; le camere si associano a un edificio.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setEditing('new')}
          data-testid="edificio-add"
          className="bg-accent-primary text-text-on-accent px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-1.5 hover:bg-accent-primary-hover"
        >
          <Plus size={15} /> Aggiungi edificio
        </button>
      </header>

      <div className="border border-border-base rounded-xl overflow-hidden bg-bg-card">
        <table className="w-full text-sm">
          <thead className="bg-bg-base">
            <tr className="text-left text-[11px] uppercase tracking-[0.5px] text-text-muted">
              <th className="px-4 py-2 font-semibold">Codice</th>
              <th className="px-4 py-2 font-semibold">Label</th>
              <th className="px-4 py-2 font-semibold">Camere</th>
              <th className="px-4 py-2 font-semibold">Ordine</th>
              <th className="px-4 py-2 font-semibold text-right">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {buildings.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-text-muted">
                  Nessun edificio. Aggiungi il primo.
                </td>
              </tr>
            )}
            {buildings.map((b) => {
              const count = countActiveRooms(b.code);
              return (
                <tr
                  key={b.id}
                  data-testid={`edificio-row-${b.code}`}
                  className="border-t border-border-base"
                >
                  <td className="px-4 py-3 font-mono text-text-primary">{b.code}</td>
                  <td className="px-4 py-3 font-semibold text-text-primary">{b.label}</td>
                  <td className="px-4 py-3 text-text-secondary">{count}</td>
                  <td className="px-4 py-3 text-text-secondary">{b.displayOrder}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => setEditing(b)}
                        data-testid={`edificio-edit-${b.code}`}
                        className="p-1.5 rounded-md hover:bg-bg-base text-text-secondary"
                        aria-label="Modifica"
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeleteTarget(b)}
                        disabled={count > 0}
                        data-testid={`edificio-delete-${b.code}`}
                        title={
                          count > 0 ? `${count} camere assegnate. Riassegnale prima.` : 'Elimina'
                        }
                        className="p-1.5 rounded-md hover:bg-[#FAEAE8] text-text-secondary hover:text-[#A8453A] disabled:opacity-40 disabled:cursor-not-allowed"
                        aria-label="Elimina"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {editing && (
        <BuildingEditModal
          target={editing === 'new' ? null : editing}
          onClose={onClose}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['buildings'] });
            onClose();
          }}
          errMsg={errMsg}
          setErrMsg={setErrMsg}
        />
      )}

      {deleteTarget && (
        <DeleteConfirmModal
          building={deleteTarget}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={() => deleteMutation.mutate(deleteTarget.id)}
          loading={deleteMutation.isPending}
          errMsg={errMsg}
        />
      )}
    </section>
  );
}

// --------------------------- BuildingEditModal ---------------------------

function BuildingEditModal({
  target,
  onClose,
  onSaved,
  errMsg,
  setErrMsg,
}: {
  target: Building | null;
  onClose: () => void;
  onSaved: () => void;
  errMsg: string | null;
  setErrMsg: (m: string | null) => void;
}) {
  const isNew = target === null;
  const [code, setCode] = useState(target?.code ?? '');
  const [label, setLabel] = useState(target?.label ?? '');
  const [displayOrder, setDisplayOrder] = useState(String(target?.displayOrder ?? 0));
  const [active, setActive] = useState(target?.active ?? true);

  const mutation = useMutation({
    mutationFn: () => {
      if (isNew) {
        return createBuilding({ code, label, displayOrder: Number(displayOrder) });
      }
      return updateBuilding(target.id, {
        code,
        label,
        displayOrder: Number(displayOrder),
        active,
      });
    },
    onSuccess: onSaved,
    onError: (err: unknown) => setErrMsg(formatErr(err)),
  });

  return (
    <ModalShell title={isNew ? 'Nuovo edificio' : 'Modifica edificio'} onClose={onClose}>
      <div className="px-6 py-5 flex flex-col gap-4">
        <Field
          label="Codice"
          required
          value={code}
          onChange={setCode}
          placeholder="es. main, annex"
          testId="edificio-code"
        />
        <Field
          label="Label"
          required
          value={label}
          onChange={setLabel}
          placeholder="es. Edificio principale"
          testId="edificio-label"
        />
        <Field
          label="Ordine display"
          value={displayOrder}
          onChange={setDisplayOrder}
          type="number"
          testId="edificio-display-order"
        />
        {!isNew && (
          <label className="flex items-center gap-2 text-sm text-text-primary">
            <input
              type="checkbox"
              checked={active}
              onChange={(e) => setActive(e.target.checked)}
              data-testid="edificio-active"
            />
            Attivo
          </label>
        )}
        {errMsg && (
          <p role="alert" className="text-xs text-red-600">
            {errMsg}
          </p>
        )}
      </div>
      <ModalFooter
        onCancel={onClose}
        onConfirm={() => mutation.mutate()}
        loading={mutation.isPending}
      />
    </ModalShell>
  );
}

// --------------------------- DeleteConfirmModal ---------------------------

function DeleteConfirmModal({
  building,
  onCancel,
  onConfirm,
  loading,
  errMsg,
}: {
  building: Building;
  onCancel: () => void;
  onConfirm: () => void;
  loading: boolean;
  errMsg: string | null;
}) {
  return (
    <ModalShell title="Eliminare edificio?" onClose={onCancel}>
      <div className="px-6 py-5 flex flex-col gap-3">
        <p className="text-sm text-text-primary">
          Eliminare l'edificio <strong>{building.label}</strong> ({building.code})? L'edificio
          verra' marcato come non attivo.
        </p>
        {errMsg && (
          <p role="alert" className="text-xs text-red-600">
            {errMsg}
          </p>
        )}
      </div>
      <ModalFooter
        onCancel={onCancel}
        onConfirm={onConfirm}
        loading={loading}
        confirmLabel="Elimina"
        danger
      />
    </ModalShell>
  );
}

// --------------------------- shared shell ---------------------------

function ModalShell({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div
      // biome-ignore lint/a11y/useSemanticElements: overlay manuale
      role="dialog"
      aria-modal="true"
      data-testid="building-modal"
      className="fixed inset-0 z-[1000] flex items-center justify-center p-5"
      style={{ background: 'rgba(45, 36, 29, 0.5)' }}
    >
      <div
        className="bg-bg-card rounded-2xl overflow-hidden"
        style={{ width: 'min(440px, 96vw)', boxShadow: '0 24px 64px rgba(45,36,29,0.25)' }}
      >
        <header className="flex items-center justify-between px-6 py-4 border-b border-border-base">
          <h3 className="text-base font-bold text-text-primary">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Chiudi"
            className="w-8 h-8 rounded-lg bg-bg-card border border-border-base flex items-center justify-center text-text-secondary hover:bg-bg-base"
          >
            <X size={16} />
          </button>
        </header>
        {children}
      </div>
    </div>
  );
}

function ModalFooter({
  onCancel,
  onConfirm,
  loading,
  confirmLabel = 'Salva',
  danger,
}: {
  onCancel: () => void;
  onConfirm: () => void;
  loading: boolean;
  confirmLabel?: string;
  danger?: boolean;
}) {
  return (
    <footer className="px-6 py-4 border-t border-border-base flex justify-end gap-2.5 bg-bg-base">
      <button
        type="button"
        onClick={onCancel}
        disabled={loading}
        data-testid="modal-cancel"
        className="bg-bg-card text-text-secondary border border-border-base px-5 py-2 rounded-lg text-[13px] font-semibold"
      >
        Annulla
      </button>
      <button
        type="button"
        onClick={onConfirm}
        disabled={loading}
        data-testid="modal-confirm"
        className="text-white border-0 px-5 py-2 rounded-lg text-[13px] font-semibold"
        style={{ background: danger ? '#A8453A' : '#E97B3D' }}
      >
        {loading ? '...' : confirmLabel}
      </button>
    </footer>
  );
}

function Field({
  label,
  required,
  value,
  onChange,
  placeholder,
  type,
  testId,
}: {
  label: string;
  required?: boolean;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
  testId?: string;
}) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-[11px] uppercase tracking-[0.5px] font-semibold text-text-secondary">
        {label}
        {required && <span className="text-accent-primary ml-0.5">*</span>}
      </span>
      <input
        type={type ?? 'text'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testId}
        className="px-3.5 py-2.5 rounded-lg border border-border-input bg-bg-card text-sm text-text-primary"
      />
    </label>
  );
}

function formatErr(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.code === 'has_active_rooms') {
      const cnt = (err.body as { activeRoomsCount?: number })?.activeRoomsCount;
      return `Edificio ha ${cnt ?? '?'} camere attive. Riassegnale prima.`;
    }
    if (err.code === 'code_already_exists') return 'Codice gia in uso.';
    return `Errore ${err.status}${err.code ? ` (${err.code})` : ''}`;
  }
  if (err instanceof Error) return err.message;
  return 'Errore sconosciuto';
}
